import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2, Paperclip } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { kindLabel, when, type EmailFile, type SentEmailRow } from '@/lib/outbox';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';

async function downloadAttachment(emailId: string, file: EmailFile) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get(`/global-admin/outbox/${emailId}/attachments/${file.id}/`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url; a.download = file.filename; a.click();
  URL.revokeObjectURL(url);
}

function kb(size: number) {
  return size < 1024 ? `${size} B` : `${Math.round(size / 1024)} KB`;
}

function Attachments({ email }: { email: SentEmailRow }) {
  if (!email.files?.length) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-2" data-testid={`email-attachments-${email.id}`}>
      {email.files.map((file) => (
        <Button
          key={file.id}
          size="sm"
          variant="outline"
          className="h-7 gap-1.5 text-xs"
          onClick={(e) => { e.stopPropagation(); downloadAttachment(email.id, file); }}
          data-testid={`email-attachment-${file.id}`}
        >
          <Download className="h-3 w-3" />
          {file.filename}
          <span className="text-muted-foreground">{kb(file.size)}</span>
        </Button>
      ))}
    </div>
  );
}

function Body({ id }: { id: string }) {
  const detail = useQuery({
    queryKey: ['outbox-email', id],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get(`/global-admin/outbox/${id}/`)).data as SentEmailRow;
    },
  });
  if (detail.isLoading) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  return (
    <pre className="whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs leading-relaxed" data-testid={`email-body-${id}`}>
      {detail.data?.body_text || '(no text body)'}
    </pre>
  );
}

export default function EmailTable({ emails, showCustomer = true }: { emails: SentEmailRow[]; showCustomer?: boolean }) {
  const [open, setOpen] = React.useState<string | null>(null);

  if (emails.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground" data-testid="outbox-empty">No emails match.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th className="py-1 w-6" />
            <th className="py-1">Sent</th>
            <th className="py-1">To</th>
            {showCustomer && <th className="py-1">Customer</th>}
            <th className="py-1">Kind</th>
            <th className="py-1">Subject</th>
            <th className="py-1 text-right">Delivery</th>
          </tr>
        </thead>
        <tbody>
          {emails.map((email) => (
            <React.Fragment key={email.id}>
              <tr
                className="cursor-pointer border-t align-top hover:bg-muted/30"
                onClick={() => setOpen(open === email.id ? null : email.id)}
                data-testid={`email-${email.id}`}
              >
                <td className="py-2 text-muted-foreground">{open === email.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</td>
                <td className="whitespace-nowrap py-2 text-xs text-muted-foreground">{when(email.sent_at)}</td>
                <td className="py-2 text-xs">{email.to}</td>
                {showCustomer && <td className="py-2 text-xs">{email.organization_name || (email.organization_id ? email.organization_id.slice(0, 8) : '—')}</td>}
                <td className="py-2"><Badge variant="outline" className="font-normal">{kindLabel(email.kind)}</Badge></td>
                <td className="py-2">
                  {email.subject}
                  {email.attachments.length > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground" title="Open the row to download">
                      <Paperclip className="h-3 w-3" />{email.attachments.join(', ')}
                    </span>
                  )}
                </td>
                <td className="py-2 text-right">
                  {email.delivered ? (
                    <Badge className="bg-emerald-500/15 text-emerald-700">delivered</Badge>
                  ) : (
                    <Badge className="bg-red-500/15 text-red-700" title={email.error}>failed</Badge>
                  )}
                </td>
              </tr>
              {open === email.id && (
                <tr className="border-t bg-muted/10">
                  <td />
                  <td colSpan={showCustomer ? 6 : 5} className="py-3">
                    {!email.delivered && email.error && (
                      <p className="mb-2 text-xs text-red-700">{email.error}</p>
                    )}
                    <Attachments email={email} />
                    <Body id={email.id} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
